/**
 * Content Service for The Salty Mermaid
 * 
 * Manages site content that can be updated from the admin panel
 */

export interface SiteContent {
  hero: {
    title: string
    subtitle: string
    lastUpdated: string
  }
  about: {
    mission: string
    story: string
    lastUpdated: string
  }
  contact: {
    address: string
    phone: string
    email: string
    hours: string
    lastUpdated: string
  }
  settings: {
    squareEnabled: boolean
    shopifyEnabled: boolean
    sharepointEnabled: boolean
    lastUpdated: string
  }
}

const DEFAULT_CONTENT: SiteContent = {
  hero: {
    title: 'Welcome to The Salty Mermaid',
    subtitle: 'A stunning art gallery and studio featuring original artworks from over 75 talented local artists. Join our creative community in Ormond Beach, right across from the beach!',
    lastUpdated: new Date().toISOString()
  },
  about: {
    mission: 'Our mission is to provide a creative space where local artists can showcase their talents and art enthusiasts can discover unique coastal-inspired pieces.',
    story: 'Located in beautiful Ormond Beach, Florida, The Salty Mermaid Art Studio and Gallery has been a cornerstone of the local art community since our founding.',
    lastUpdated: new Date().toISOString()
  },
  contact: {
    address: '123 Ocean Avenue, Ormond Beach, FL 32174',
    phone: '(386) 555-0123',
    email: 'info@saltymermaidart.com',
    hours: 'Mon-Sat: 10am-6pm, Sun: 12pm-5pm',
    lastUpdated: new Date().toISOString()
  },
  settings: {
    squareEnabled: true,
    shopifyEnabled: true,
    sharepointEnabled: false,
    lastUpdated: new Date().toISOString()
  }
}

class ContentService {
  private readonly storageKey = 'salty_mermaid_content'
  private content: SiteContent = DEFAULT_CONTENT
  private listeners: (() => void)[] = []

  constructor() {
    this.loadContent()
    
    // Listen for changes from admin panel
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey && e.newValue) {
        this.content = JSON.parse(e.newValue)
        this.notifyListeners()
      }
    })
  }

  private loadContent(): void {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        this.content = { ...DEFAULT_CONTENT, ...JSON.parse(stored) }
      }
    } catch (error) {
      console.error('ContentService: Error loading content', error)
      this.content = DEFAULT_CONTENT
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener())
  }

  // Subscribe to content updates
  subscribe(callback: () => void): () => void {
    this.listeners.push(callback)
    return () => {
      const index = this.listeners.indexOf(callback)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  // Get all content
  getContent(): SiteContent {
    return { ...this.content }
  }

  // Get specific section content
  getHeroContent() {
    return { ...this.content.hero }
  }

  getAboutContent() {
    return { ...this.content.about }
  }

  getContactContent() {
    return { ...this.content.contact }
  }

  getSettings() {
    return { ...this.content.settings }
  }

  // Update content (for admin use)
  updateContent(newContent: Partial<SiteContent>): void {
    this.content = { ...this.content, ...newContent }
    localStorage.setItem(this.storageKey, JSON.stringify(this.content))
    this.notifyListeners()
  }

  // Update specific section
  updateSection<K extends keyof SiteContent>(
    section: K, 
    data: Partial<SiteContent[K]>
  ): void {
    this.content[section] = {
      ...this.content[section],
      ...data,
      lastUpdated: new Date().toISOString()
    } as SiteContent[K]
    
    localStorage.setItem(this.storageKey, JSON.stringify(this.content))
    this.notifyListeners()
  }

  // Check if content is customized (different from defaults)
  isContentCustomized(): boolean {
    return JSON.stringify(this.content) !== JSON.stringify(DEFAULT_CONTENT)
  }

  // Reset to defaults
  resetToDefaults(): void {
    this.content = { ...DEFAULT_CONTENT }
    localStorage.setItem(this.storageKey, JSON.stringify(this.content))
    this.notifyListeners()
  }
}

// Export singleton instance
export const contentService = new ContentService()

// Export types and defaults
export { DEFAULT_CONTENT }